import { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { listarConversacionesPlantelService } from "../../../services/plantels/conversaciones.service";
import Loader from "../../UI/Loader";
import Alert from "../../UI/Alert";
import CONVERSACIONES_META from "../../conversacionesGlobales/constants";
import ConversacionPlantelModal from "./ConversacionPlantelModal";

/* ------------------------------------------------------------------ */
/*  Infográfico de Conversaciones a nivel Plantel                      */
/* ------------------------------------------------------------------ */

const ConversacionesPlantelLayer = ({ plantelId }) => {
  const [conversaciones, setConversaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [apiOnline, setApiOnline] = useState(false);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalInitialMode, setModalInitialMode] = useState("view");
  const [seleccionada, setSeleccionada] = useState(null);

  /* ----- Cargar conversaciones ----- */
  const cargarConversaciones = useCallback(async () => {
    if (!plantelId) return;
    setLoading(true);
    setApiError(null);
    try {
      const data = await listarConversacionesPlantelService(plantelId);
      setConversaciones(Array.isArray(data) ? data : data?.results ?? []);
      setApiOnline(true);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Error desconocido al conectar con la API.";
      setApiError(msg);
      setApiOnline(false);
      toast.error("No se pudieron cargar las conversaciones del plantel.");
    } finally {
      setLoading(false);
    }
  }, [plantelId]);

  useEffect(() => {
    cargarConversaciones();
  }, [cargarConversaciones]);

  /* ----- Abrir modal en modo lectura ----- */
  const abrirVer = (conv) => {
    setSeleccionada(conv);
    setModalInitialMode("view");
    setModalAbierto(true);
  };

  /* ----- Abrir modal en modo edición ----- */
  const abrirEditar = (conv) => {
    setSeleccionada(conv);
    setModalInitialMode("edit");
    setModalAbierto(true);
  };

  /* ----- Cerrar modal ----- */
  const cerrarModal = () => {
    setModalAbierto(false);
    setSeleccionada(null);
  };

  /* ----- Helpers ----- */
  const metaDe = (num) =>
    CONVERSACIONES_META[num] || {
      foco: "",
      color: "#6c757d",
      colorClaro: "#f8f9fa",
      icono: "mdi:message-outline",
    };

  const conversacionPorNumero = (num) =>
    conversaciones.find((c) => c.numero_conversacion === num) || null;

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */

  return (
    <div>
      {/* Encabezado */}
      <div className="mb-3">
        <h5 className="fw-semibold">
          ¿Cómo conducir cada conversación de Mis Talentos?
        </h5>
        <p className="text-muted small mb-0">
          Configuración de las 5 conversaciones para este plantel.
        </p>
      </div>

      {/* Indicador de estado de la API */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span
          className={`badge d-flex align-items-center gap-1 py-2 px-3 rounded-pill ${
            loading
              ? "bg-warning bg-opacity-10 text-warning"
              : apiOnline
              ? "bg-success bg-opacity-10 text-success"
              : "bg-danger bg-opacity-10 text-danger"
          }`}
        >
          <span
            className={`rounded-circle d-inline-block ${
              loading
                ? "bg-warning"
                : apiOnline
                ? "bg-success"
                : "bg-danger"
            }`}
            style={{ width: 8, height: 8 }}
          />
          {loading
            ? ""
            : apiOnline
            ? ""
            : "Error de conexión"}
        </span>
        {!apiOnline && !loading && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={cargarConversaciones}
          >
            <Icon icon="mdi:refresh" width="14" className="me-1" />
            Reintentar
          </button>
        )}
      </div>

      {/* Error de API */}
      {apiError && (
        <Alert
          message={apiError}
          variant="error"
          onClose={() => setApiError(null)}
        />
      )}

      {/* Loader */}
      {loading && (
        <div className="py-5">
          <Loader center text="Cargando conversaciones..." delay={200} />
        </div>
      )}

      {/* Infográfico horizontal de 5 columnas */}
      {!loading && (
        <div className="radius-12 overflow-hidden border">
          {/* ===== Fila 1: Encabezados de columna ===== */}
          <div className="row g-0">
            {[1, 2, 3, 4, 5].map((num) => {
              const meta = metaDe(num);
              const conv = conversacionPorNumero(num);
              return (
                <div
                  key={`hdr-${num}`}
                  className="col"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: num < 5 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <div
                    className="d-flex flex-column align-items-center text-center py-4 px-3"
                    style={{ backgroundColor: meta.colorClaro }}
                  >
                    <span
                      className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                      style={{
                        width: 48,
                        height: 48,
                        backgroundColor: meta.color,
                      }}
                    >
                      <Icon icon={meta.icono} width={26} className="text-white" />
                    </span>
                    <span
                      className="fw-bold mb-1"
                      style={{ color: meta.color, fontSize: "12px" }}
                    >
                      CONVERSACIÓN {num}
                    </span>
                    <span
                      className="fw-semibold"
                      style={{
                        color: meta.color,
                        fontSize: "13px",
                        lineHeight: 1.3,
                      }}
                    >
                      {conv?.pregunta || "Sin datos"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== Fila 2: Propósito de la conversación ===== */}
          <div
            className="px-4 py-2 text-center"
            style={{
              backgroundColor: "#f0f4f8",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <span
              className="fw-bold text-uppercase"
              style={{ fontSize: "14px", letterSpacing: "0.5px", color: "#6b7280" }}
            >
              Propósito de la conversación
            </span>
          </div>
          <div className="row g-0">
            {[1, 2, 3, 4, 5].map((num) => {
              const meta = metaDe(num);
              const conv = conversacionPorNumero(num);
              return (
                <div
                  key={`pro-${num}`}
                  className="col d-flex align-items-start gap-2 py-3 px-3"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: num < 5 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <span className="flex-shrink-0 mt-1">
                    <Icon
                      icon={meta.icono}
                      width={18}
                      style={{ color: meta.color }}
                    />
                  </span>
                  <span
                    className="small"
                    style={{
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: conv?.descripcion_proposito ? "#374151" : "#9ca3af",
                      fontStyle: conv?.descripcion_proposito ? "normal" : "italic",
                    }}
                  >
                    {conv?.descripcion_proposito ||
                      "Sin contenido — usa el botón Editar para agregarlo."}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ===== Fila 3: ¿Cómo la utilizará el Mentor? ===== */}
          <div
            className="px-4 py-2 text-center"
            style={{
              backgroundColor: "#f0f4f8",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <span
              className="fw-bold text-uppercase"
              style={{ fontSize: "14px", letterSpacing: "0.5px", color: "#6b7280" }}
            >
              ¿Cómo la utilizará el Mentor?
            </span>
          </div>
          <div className="row g-0">
            {[1, 2, 3, 4, 5].map((num) => {
              const meta = metaDe(num);
              const conv = conversacionPorNumero(num);
              return (
                <div
                  key={`uti-${num}`}
                  className="col d-flex align-items-start gap-2 py-3 px-3"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: num < 5 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <span className="flex-shrink-0 mt-1">
                    <Icon
                      icon={meta.icono}
                      width={18}
                      style={{ color: meta.color }}
                    />
                  </span>
                  <span
                    className="small"
                    style={{
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: conv?.descripcion_utilizar ? "#374151" : "#9ca3af",
                      fontStyle: conv?.descripcion_utilizar ? "normal" : "italic",
                    }}
                  >
                    {conv?.descripcion_utilizar ||
                      "Sin contenido — usa el botón Editar para agregarlo."}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ===== Fila 4: Botones de edición por columna ===== */}
          <div
            className="row g-0"
            style={{ borderTop: "1px solid #e5e7eb" }}
          >
            {[1, 2, 3, 4, 5].map((num) => {
              const meta = metaDe(num);
              const conv = conversacionPorNumero(num);
              return (
                <div
                  key={`btns-${num}`}
                  className="col py-3 px-3 d-flex flex-column align-items-center gap-2"
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    borderRight: num < 5 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <div className="d-flex gap-1 w-100">
                    <button
                      className="btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                      style={{
                        border: `1px solid ${meta.color}`,
                        color: meta.color,
                        fontSize: "12px",
                      }}
                      onClick={() => {
                        if (conv) abrirVer(conv);
                        else
                          toast.info(
                            "Esta conversación aún no tiene datos en la API."
                          );
                      }}
                      disabled={!conv}
                    >
                      <Icon icon="mdi:eye-outline" width="14" />
                      Ver
                    </button>
                    <button
                      className="btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1 text-white"
                      style={{
                        backgroundColor: meta.color,
                        fontSize: "12px",
                      }}
                      onClick={() => {
                        if (conv) abrirEditar(conv);
                        else
                          toast.info(
                            "Esta conversación aún no tiene datos en la API."
                          );
                      }}
                      disabled={!conv}
                    >
                      <Icon icon="mdi:pencil-outline" width="14" />
                      Editar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            className="d-flex align-items-center justify-content-between gap-2 px-4 py-2"
            style={{
              backgroundColor: "#f9fafb",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <span style={{ fontSize: "11px", color: "#6b7280" }}>
              <Icon icon="mdi:information-outline" width="14" className="me-1" />
              Los cambios se guardan individualmente por conversación.
            </span>
          </div>
        </div>
      )}

      {/* Modal: Ver / Editar */}
      <ConversacionPlantelModal
        open={modalAbierto}
        onClose={cerrarModal}
        conversacion={seleccionada}
        initialMode={modalInitialMode}
        onSaved={cargarConversaciones}
      />
    </div>
  );
};

export default ConversacionesPlantelLayer;
